import { useMemo, type ReactNode } from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { rehypeCashtags } from '@/components/cashtag-highlight'

/** The text inside a fenced block, read off the hast node the `pre` override receives. */
function codeText(node: unknown): string {
  const out: string[] = []
  const walk = (n: unknown) => {
    if (!n || typeof n !== 'object') return
    const el = n as { type?: string; value?: string; children?: unknown[] }
    if (el.type === 'text' && typeof el.value === 'string') out.push(el.value)
    el.children?.forEach(walk)
  }
  walk(node)
  return out.join('')
}

/** A fenced block's language ("chart" for ```chart), read off the hast `pre` node. */
function codeLang(node: unknown): string {
  const code = (node as { children?: Array<{ properties?: { className?: unknown } }> })
    ?.children?.[0]
  const cls = code?.properties?.className
  const list = Array.isArray(cls) ? cls : typeof cls === 'string' ? [cls] : []
  const lang = list.map(String).find((c) => c.startsWith('language-'))
  return lang ? lang.slice('language-'.length) : ''
}

// Shared react-markdown component overrides for every surface: wide content
// (tables, code) scrolls inside its container instead of blowing out the
// layout, and links open in a new tab. Surface-specific typography is supplied
// by the caller via `className` (the `prose …` sizing).
const MARKDOWN_COMPONENTS = {
  table: ({ node: _n, ...props }: { node?: unknown }) => (
    <div className="overflow-x-auto">
      <table {...props} />
    </div>
  ),
  pre: ({ node: _n, ...props }: { node?: unknown }) => (
    <pre className="overflow-x-auto" {...props} />
  ),
  a: ({ node: _n, ...props }: { node?: unknown }) => (
    <a target="_blank" rel="noopener noreferrer" {...props} />
  ),
}

export interface AppMarkdownProps {
  children: string
  /** Wrapper class — typically the `prose …` sizing for the surface. */
  className?: string
  /**
   * Something to render under a fenced code block, given its text (an "Open on
   * chart" button for a script, say). Return null for blocks that get nothing.
   */
  codeBlockAction?: (code: string) => ReactNode
  /**
   * Replace a fenced block entirely, given its language and text (the chat
   * draws ```chart blocks). Return null to render the block as code.
   */
  renderCodeBlock?: (lang: string, code: string) => ReactNode | null
  /** Highlight ticker symbols as `$SYMBOL` chips (chat surfaces). */
  highlightTickers?: boolean
  /** Conversation-established symbols so lowercase mentions highlight too. */
  knownTickers?: ReadonlySet<string>
}

/**
 * App-wide markdown renderer: react-markdown + `remark-gfm` (tables,
 * strikethrough, autolinks) with the shared table-scroll / new-tab-link
 * overrides. Use this for ALL rendered markdown so gfm + link/overflow behavior
 * stay consistent — several surfaces previously omitted `remark-gfm` and
 * silently dropped tables.
 */
export function AppMarkdown({
  children,
  className,
  highlightTickers,
  knownTickers,
  codeBlockAction,
  renderCodeBlock,
}: AppMarkdownProps) {
  const components = useMemo(
    () =>
      codeBlockAction || renderCodeBlock
        ? {
            ...MARKDOWN_COMPONENTS,
            pre: ({ node, ...props }: { node?: unknown }) => {
              const replaced = renderCodeBlock?.(codeLang(node), codeText(node).trim()) ?? null
              if (replaced !== null) return <>{replaced}</>
              return (
                <>
                  <pre className="overflow-x-auto" {...props} />
                  {codeBlockAction?.(codeText(node))}
                </>
              )
            },
          }
        : MARKDOWN_COMPONENTS,
    [codeBlockAction, renderCodeBlock],
  )
  return (
    <div className={className}>
      <Markdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={highlightTickers ? [[rehypeCashtags, { known: knownTickers }]] : []}
        components={components}
      >
        {children}
      </Markdown>
    </div>
  )
}
